import { useState, useCallback, useEffect } from 'react';

import debounce from 'lodash/debounce';

import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { Skeleton } from '../../../../../Skeleton';
import { InfiniteScroll } from '../../../../../InfiniteScroll';
import { useAdditionalFetchDataHelpersPluginCallback } from '../../AdditionalFetchDataHelpersPlugin';

import { UserItem } from './UserItem';

interface User {
  id: string;
  type: 'user';
  data: {
    id: string;
    first_name: string;
    second_name: string;
    middle_name: string;
    logo?: string;
  };
}

interface Unit {
  id: string;
  type: 'unit';
  data: { id: string; name: string };
}

type Entity = User | Unit;

interface UsersListProps {
  inputValue: string;
  handleSelectValue: (value: {
    name: string;
    id: string;
    type: 'unit' | 'user';
    logo?: string;
  }) => void;
}

const LIMIT = 20;

export const UsersList = ({
  inputValue,
  handleSelectValue,
}: UsersListProps) => {
  const contextHelpers = useAdditionalFetchDataHelpersPluginCallback();
  const [data, setData] = useState<Entity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadUsers = useCallback(
    () =>
      debounce((newOffset: number, savePrev: boolean) => {
        setOffset(() => newOffset);

        if (contextHelpers?.searchMentions) {
          setIsLoading(true);

          contextHelpers
            .searchMentions({
              query: inputValue,
              offset: newOffset,
              limit: LIMIT,
            })
            .then((res) => {
              setTotal(() => res.total);
              setData((prev) =>
                savePrev
                  ? [...prev, ...(res.mentions as Entity[])]
                  : (res.mentions as Entity[]),
              );
              setIsLoading(() => false);
            });
        }
      }, 300),
    [contextHelpers, inputValue],
  )();

  const loadNextUsers = useCallback(() => {
    if (!isLoading) {
      loadUsers(offset + LIMIT, true);
    }
  }, [isLoading, loadUsers, offset]);

  useEffect(() => {
    loadUsers(0, false);

    return () => {
      loadUsers.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  if (isLoading && !data.length) {
    return (
      <Box paddingY="8" width={290}>
        <Box px="16" py="12" display="flex" alignItems="center" gap="8">
          <Skeleton variant="circle" height={24} width={24} />

          <Skeleton variant="rect" width="calc(100% - 30px)" height={24} />
        </Box>
      </Box>
    );
  }

  if (!data.length) {
    return (
      <Box paddingY="8" width={290}>
        <Box px="16" py="12" display="flex" alignItems="center" gap="8">
          <Typography variant="body3" color="text.light.tertirary">
            Ничего не найдено
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      paddingY="8"
      width={290}
      maxHeight={256}
      overflow="auto"
      id="mention-users-list"
    >
      <InfiniteScroll
        dataLength={data.length}
        next={loadNextUsers}
        hasMore={data.length < total && total > LIMIT}
        loader={
          isLoading ? (
            <Box px="16" py="12" display="flex" alignItems="center" gap="8">
              <Skeleton variant="circle" height={24} width={24} />

              <Skeleton variant="rect" width="100%" height={24} />
            </Box>
          ) : null
        }
        endMessage={<div />}
        scrollId="mention-users-list"
      >
        <Box>
          {data.map((item, index) => (
            <UserItem
              data={item.data}
              key={item.id + index}
              inputValue={inputValue}
              handleSelectValue={handleSelectValue}
            />
          ))}
        </Box>
      </InfiniteScroll>
    </Box>
  );
};
